import { isRichTextEmpty, replaceTemplatePlaceholders, richTextToPlainText } from "./riskReviewTemplate";
import { normalizeControlEffectivenessKey } from "./controlEffectiveness";

export const CONTROL_REVIEW_FIELDS = [
  "last_review_date",
  "last_reviewed_by",
  "review_outcome",
  "effectiveness_rating",
];

const FIELD_ORDER = [
  "effectiveness_rating",
  "review_outcome",
  "last_reviewed_by",
  "last_review_date",
];

const EMPTY_LABELS = {
  last_review_date: "not set",
  last_reviewed_by: "not set",
  review_outcome: "not assessed",
  effectiveness_rating: "not assessed",
};

const toDisplayText = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const withSpaces = raw.replace(/_/g, " ");
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

export const formatControlReviewDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  const day = String(parsed.getUTCDate()).padStart(2, "0");
  const month = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  const year = String(parsed.getUTCFullYear());
  return `${day}/${month}/${year}`;
};

export const formatControlReviewValue = (field, value) => {
  if (field === "last_review_date") {
    return formatControlReviewDate(value) || EMPTY_LABELS.last_review_date;
  }
  if (field === "last_reviewed_by") {
    const reviewer = String(value || "").trim();
    return reviewer || EMPTY_LABELS.last_reviewed_by;
  }
  if (field === "effectiveness_rating") {
    const normalizedKey = normalizeControlEffectivenessKey(value, { blankKey: "" });
    if (normalizedKey === "needs_improvement") {
      return "Partially Effective";
    }
  }
  return toDisplayText(value) || EMPTY_LABELS[field] || "not set";
};

const buildFieldSentence = (field, change) => {
  const fromValue = formatControlReviewValue(field, change?.from);
  const toValue = formatControlReviewValue(field, change?.to);
  if (field === "effectiveness_rating") {
    return `Effectiveness changed from ${fromValue} to ${toValue}.`;
  }
  if (field === "review_outcome") {
    return `Review outcome changed from ${fromValue} to ${toValue}.`;
  }
  if (field === "last_reviewed_by") {
    return `Reviewer changed from ${fromValue} to ${toValue}.`;
  }
  if (field === "last_review_date") {
    return `Review date changed from ${fromValue} to ${toValue}.`;
  }
  return "";
};

export const hasControlReviewChanges = (changes) =>
  CONTROL_REVIEW_FIELDS.some((field) => Boolean(changes?.[field]));

export const buildControlReviewChangeLines = (changes) => {
  const lines = [];
  FIELD_ORDER.forEach((field) => {
    if (!changes?.[field]) return;
    const sentence = buildFieldSentence(field, changes[field]);
    if (sentence) lines.push(sentence);
  });
  return lines;
};

export const buildControlReviewNote = ({ changes, nextValues, currentValues } = {}) => {
  const reviewDate =
    changes?.last_review_date?.to ||
    nextValues?.last_review_date ||
    currentValues?.last_review_date ||
    "";
  const formattedReviewDate = formatControlReviewDate(reviewDate);

  const lines = [formattedReviewDate ? `Control review on ${formattedReviewDate}.` : "Control review recorded."];
  lines.push(...buildControlReviewChangeLines(changes));

  return lines.join("\n");
};

export const buildControlReviewTemplateVariables = ({ control, changes, nextValues, currentValues } = {}) => {
  const reviewDateRaw =
    changes?.last_review_date?.to ||
    nextValues?.last_review_date ||
    currentValues?.last_review_date ||
    "";
  const reviewDate = formatControlReviewDate(reviewDateRaw);
  const reviewerRaw =
    changes?.last_reviewed_by?.to ||
    nextValues?.last_reviewed_by ||
    currentValues?.last_reviewed_by ||
    "";
  const reviewLines = buildControlReviewChangeLines(changes);

  return {
    review_date: reviewDate,
    control_title: String(control?.name || nextValues?.name || currentValues?.name || "").trim(),
    control_id: String(control?.id || nextValues?.id || currentValues?.id || "").trim(),
    reviewer: String(reviewerRaw || "").trim() || EMPTY_LABELS.last_reviewed_by,
    review_outcome_from: formatControlReviewValue("review_outcome", changes?.review_outcome?.from),
    review_outcome_to: formatControlReviewValue("review_outcome", changes?.review_outcome?.to),
    effectiveness_from: formatControlReviewValue("effectiveness_rating", changes?.effectiveness_rating?.from),
    effectiveness_to: formatControlReviewValue("effectiveness_rating", changes?.effectiveness_rating?.to),
    review_changes_summary: reviewLines.join("\n"),
  };
};

export const renderControlReviewNoteFromTemplate = ({
  templateBody,
  control,
  changes,
  nextValues,
  currentValues,
} = {}) => {
  if (!templateBody || isRichTextEmpty(templateBody)) {
    return buildControlReviewNote({ control, changes, nextValues, currentValues });
  }
  const variables = buildControlReviewTemplateVariables({ control, changes, nextValues, currentValues });
  const renderedTemplate = replaceTemplatePlaceholders(templateBody, variables);
  const plainText = richTextToPlainText(renderedTemplate);
  return plainText || buildControlReviewNote({ control, changes, nextValues, currentValues });
};
